///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

//-----------------------------------------------------------------------------------------
//* \brief
//? Resolves the BNR EUR rate an invoice is priced at.
//?
//? The rate is frozen onto the invoice and the card payment is later computed from it, so
//? being wrong here means charging the wrong amount and issuing a wrong fiscal document.
//?
//? Order: the backend (which walks Firestore backwards and can fall back to BNR live),
//? then a direct Firestore read from here in case the backend is unreachable, then a
//? hardcoded rate as an absolute last resort.
//-----------------------------------------------------------------------------------------

import { debugMsgLog } from './LoggingService'

// Last resort only, and every use is reported. Keep in step with FALLBACK_EUR_RATE in
// functions/services/exchangeRateService.js
export const FALLBACK_EUR_RATE = 5.2419
export const FALLBACK_RATE_DATE = '2026-08-17'

const MAX_LOOKBACK_DAYS = 10

const pad = (nr) => (nr > 9 ? `${nr}` : `0${nr}`)
const dateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
const monthKey = (date) => `${date.getMonth() + 1}-${date.getFullYear()}`

const daysBefore = (date, days) => {
    const copy = new Date(date)
    copy.setDate(copy.getDate() - days)
    return copy
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Pulls the EUR rate for one day out of a month document, never throwing when the day,
//? the currency or the document itself is missing.
//-----------------------------------------------------------------------------------------
const readRate = (monthData, key) => {
    if (!monthData) return NaN
    const day = monthData[key]
    if (!day || !day.EUR) return NaN
    const rate = Number(day.EUR.rate)
    return isFinite(rate) && rate > 0 ? rate : NaN
}

//-----------------------------------------------------------------------------------------
//* \brief
//? Most recent rate at or before startDate, read straight from Firestore.
//?
//? Checks every document matching a month rather than the last one: a month can carry two
//? documents since the August 2026 change of document ids, and only one of them holds the
//? recent days.
//-----------------------------------------------------------------------------------------
export const rateFromFirestore = async (db, startDate, lookbackDays = MAX_LOOKBACK_DAYS) => {
    const monthsRead = new Map()

    for (let back = 0; back < lookbackDays; back++) {
        const day = daysBefore(startDate, back)
        const month = monthKey(day)

        if (!monthsRead.has(month)) {
            const results = await db.collection('bnr_exchange_rates')
                .where('month-year', '==', month).get()
            const docs = []
            results.forEach(res => docs.push(res.data()))
            monthsRead.set(month, docs)
        }

        const key = dateKey(day)
        for (const monthData of monthsRead.get(month)) {
            const rate = readRate(monthData, key)
            if (!isNaN(rate)) return { rate, date: key, source: 'firestore' }
        }
    }

    return null
}

//-----------------------------------------------------------------------------------------
//* \brief
//? The rate to price an invoice with, as { rate, date, source }.
//? Starts one day back, keeping the long standing rule of invoicing at the previous
//? business day's rate, then walks backwards over weekends and holidays.
//-----------------------------------------------------------------------------------------
export const resolveEurRate = async ({ db, apiUrl }) => {
    // 1. the backend - the only place that can also rescue itself from BNR directly
    try {
        const response = await fetch(`${apiUrl}exchange-rate/eur`)
        if (response.ok) {
            const result = await response.json()
            const rate = Number(result.rate)
            if (isFinite(rate) && rate > 0) return { ...result, rate }
        }
    }
    catch (err) {
        debugMsgLog('Exchange rate endpoint unreachable, reading Firestore directly')
    }

    // 2. Firestore, in case the backend is down
    try {
        const stored = await rateFromFirestore(db, daysBefore(new Date(), 1))
        if (stored) return stored
    }
    catch (err) {
        debugMsgLog(err)
    }

    // 3. nothing worked
    return { rate: FALLBACK_EUR_RATE, date: FALLBACK_RATE_DATE, source: 'fallback' }
}
